/* eslint-disable react/button-has-type */
import L from 'leaflet'
import PropTypes from 'prop-types'
import MarkerClusterGroup from 'react-leaflet-cluster'
import PinComponent from '../../../components/stories/atoms/pins'
import { pinsStatusVariantOptions } from '../../../components/stories/atoms/pins/constants'
import ReactDOMServer from 'react-dom/server'
import { v4 as uuid } from 'uuid'

const pinStatus = {
  OPEN: 'Open',
  CLOSED: 'Closed',
  INACTIVE: 'Inactive'
}

const PinIndicatorCurrent = {
  OPEN: 'Open',
  DONE: 'Done',
  REJECTED: 'Rejected',
  CLOSED: 'Closed',
  INACTIVE: 'Inactive',
  DUE: 'Due'
}

export const handleIconIndicator = (state, indicator, progress) => {
  if (state === pinStatus.CLOSED) {
    return pinsStatusVariantOptions.CLOSED
  }

  if (progress === 100) {
    return pinsStatusVariantOptions.COMPLETED
  }

  // Mapping indicators to corresponding statuses
  const statusMap = {
    [PinIndicatorCurrent.OPEN]: pinsStatusVariantOptions.PROGRESS,
    [PinIndicatorCurrent.DUE]: pinsStatusVariantOptions.OVERDUE,
    [PinIndicatorCurrent.REJECTED]: pinsStatusVariantOptions.REJECTED
  }

  // Returns the corresponding status or a default value
  return statusMap[indicator] || pinsStatusVariantOptions.PROGRESS
}

export const handleIconPinWithoutIndicator = (state) => {
  if (state === pinStatus.CLOSED) {
    return pinsStatusVariantOptions.CLOSED
  }

  return undefined
}

export const generateMapPinIcon = (pin, reduced, selected) => {
  const uid = `pin-item-${uuid()}`

  const handleGenerate = (hasTasks) => {
    if (hasTasks) {
      return ReactDOMServer.renderToString(
        <PinComponent
          variant={`gradient-${pin.color.toLowerCase()}`}
          gradientId={uid}
          isInactive={
            (reduced || (pin.state && pin.state === 'Inactive')) && !selected
          }
          percentage={pin.progress}
          size={60}
          iconStatus={handleIconIndicator(
            pin.state,
            pin.indicatorCurrent,
            pin.progress
          )}
        />
      )
    } else {
      return ReactDOMServer.renderToString(
        <PinComponent
          variant={`gradient-${pin.color.toLowerCase()}`}
          gradientId={uid}
          isInactive={
            (reduced || (pin.state && pin.state === 'Inactive')) && !selected
          }
          size={60}
          iconStatus={handleIconPinWithoutIndicator(pin.state)}
        />
      )
    }
  }

  const hasTasks = pin.totalTasks > 0

  const html = handleGenerate(hasTasks)

  const divIcon = L.divIcon({
    iconSize: new L.Point(60, 60),
    className: '',
    html,
    iconAnchor: [30, 60]
  })

  return divIcon
}

export const generatePinPosition = ({ x, y }, map) =>
  map.unproject([x, y], map.getMaxZoom())

export const generatePinClusterOptions = (maxLevel) => {
  const uid = `pin-cluster-${uuid()}`

  const handlePinCluster = (cluster) =>
    ReactDOMServer.renderToString(
      <PinComponent
        isInactive={false}
        counter={Number(cluster.getChildCount())}
        colorCounter={'white'}
        size={60}
        gradientId={uid}
      />
    )

  return {
    spiderfyOnMaxZoom: false,
    showCoverageOnHover: false,
    disableClusteringAtZoom: maxLevel,
    animateAddingMarkers: true,
    animate: true,
    maxClusterRadius: 40,
    iconCreateFunction: (cluster) =>
      L.divIcon({
        iconSize: new L.Point(60, 60),
        className: '',
        html: handlePinCluster(cluster),
        iconAnchor: [30, 60]
      })
  }
}

export const generateNewPinPosition = (map, fullBounds) => {
  const center = map.getCenter()

  let { lat } = center
  let { lng } = center
  const northEastLat = fullBounds._northEast.lat // eslint-disable-line no-underscore-dangle
  const southWestLat = fullBounds._southWest.lat // eslint-disable-line no-underscore-dangle
  const northEastLng = fullBounds._northEast.lng // eslint-disable-line no-underscore-dangle
  const southWestLng = fullBounds._southWest.lng // eslint-disable-line no-underscore-dangle
  if (lat > northEastLat) {
    lat = northEastLat
  } else if (lat < southWestLat) {
    lat = southWestLat
  }
  if (lng > northEastLng) {
    lng = northEastLng
  } else if (lng < southWestLng) {
    lng = southWestLng
  }

  return [lat, lng]
}

// noinspection JSPotentiallyInvalidConstructorUsage
export const generateNewPinIcon = (color) => {
  const uid = `pin-new-${uuid()}`
  const html = ReactDOMServer.renderToString(
    <PinComponent
      variant={`gradient-${color?.toLowerCase() ?? 'orange'}`}
      size={60}
      gradientId={uid}
    />
  )

  const divIcon = L.divIcon({
    iconSize: new L.Point(60, 60),
    className: '',
    html,
    iconAnchor: [30, 60]
  })

  return divIcon
}

export const generateMovePinIcon = (pin) => {
  const uid = `pin-move-${uuid()}`
  const html = ReactDOMServer.renderToString(
    <PinComponent
      variant={`gradient-${pin.color.toLowerCase()}`}
      isInactive={pin.state && pin.state === 'Inactive'}
      percentage={pin.progress}
      size={60}
      gradientId={uid}
      iconStatus={handleIconIndicator(
        pin.state,
        pin.indicatorCurrent,
        pin.progress
      )}
    />
  )

  return L.divIcon({
    iconSize: new L.Point(60, 60),
    className: '',
    html,
    iconAnchor: [30, 60]
  })
}

export const PinCluster = ({ cluster, options, children }) =>
  cluster ? (
    <MarkerClusterGroup {...options}>{children}</MarkerClusterGroup>
  ) : (
    <div>{children}</div>
  )

PinCluster.propTypes = {
  cluster: PropTypes.bool,
  options: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired
}
